import React, { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import axios from 'axios';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send,
  CheckCircle,
  User,
  Building2,
  Package,
  Clock,
  MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productType: '',
    quantity: '',
    message: '',
    honeypot: ''
  });

  const formRef = useRef(null);
  const contactCardsRef = useRef([]);

  useEffect(() => {
    // Animate contact cards
    if (contactCardsRef.current.length > 0) {
      gsap.from(contactCardsRef.current, {
        y: 30,
        opacity: 0,
        stagger: 0.15,
        duration: 0.6,
        ease: 'power2.out',
        delay: 0.3
      });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        productType: formData.productType,
        quantity: formData.quantity,
        message: formData.message,
        pageSource: 'contact',
        honeypot: formData.honeypot
      });

      if (response.data.success) {
        toast.success(response.data.message, {
          description: "We'll get back to you soon!",
        });

        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          productType: '',
          quantity: '',
          message: '',
          honeypot: ''
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error("Submission Failed", {
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Call Us",
      detail: "+91 88790 49450",
      subtext: "Monday - Saturday, 9:00 AM - 6:00 PM",
      link: "tel:+918879049450"
    },
    {
      icon: Mail,
      title: "Email Us",
      detail: "Sagarpack75@gmail.com",
      subtext: "We respond within 24 hours",
      link: "mailto:Sagarpack75@gmail.com"
    },
    {
      icon: MapPin,
      title: "Visit Us",
      detail: "Mumbai, Maharashtra",
      subtext: "12, Pearl Glass compound, IB Patel road, Goregaon East, Mumbai 400063",
      link: "#location"
    }
  ];

  const benefits = [
    "Free design consultation",
    "Physical samples before production",
    "Flexible order quantities",
    "Transparent pricing",
    "On-time delivery guarantee"
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <p className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Get In Touch</p>
            <h1 className="text-5xl lg:text-6xl font-bold text-navy-900 leading-tight">
              Let's Discuss Your
              <br />
              <span className="text-gray-600">Packaging Needs</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Reach out to explore how we can support your packaging requirements. Our team is ready to provide guidance and solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-6">
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <Card 
                  key={index}
                  ref={el => contactCardsRef.current[index] = el}
                  className="border-2 hover:shadow-xl transition-all duration-300"
                >
                  <CardContent className="p-6 space-y-4">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-navy-900 rounded-lg">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="font-semibold text-lg text-navy-900">{info.title}</h3>
                    {info.link.startsWith('#') ? (
                      <>
                        <p className="text-navy-900 font-medium">{info.detail}</p>
                        <p className="text-sm text-gray-600 leading-relaxed">{info.subtext}</p>
                      </>
                    ) : (
                      <>
                        <a 
                          href={info.link}
                          className="block text-navy-900 font-medium hover:text-gray-700 transition-colors"
                          target={info.link.startsWith('http') ? '_blank' : undefined}
                          rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                        >
                          {info.detail}
                        </a>
                        <p className="text-sm text-gray-600">{info.subtext}</p>
                      </>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form - Takes 3 columns */}
            <div className="lg:col-span-3" ref={formRef}>
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold text-navy-900 mb-2">
                      Send Us a Message
                    </h2>
                    <p className="text-gray-600">
                      Fill out the form and we'll get back to you within 24 hours
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot field */}
                    <input
                      type="text"
                      name="honeypot"
                      value={formData.honeypot}
                      onChange={handleChange}
                      style={{ display: 'none' }}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-navy-900" />
                          <span>Full Name *</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          required
                          className="border-gray-300 focus:border-navy-900 focus:ring-navy-900"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email" className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-navy-900" />
                          <span>Email *</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@company.com"
                          required
                          className="border-gray-300 focus:border-navy-900 focus:ring-navy-900"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="flex items-center space-x-2">
                          <Phone className="h-4 w-4 text-navy-900" />
                          <span>Phone *</span>
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 98765 43210"
                          required
                          className="border-gray-300 focus:border-navy-900 focus:ring-navy-900"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="company" className="flex items-center space-x-2">
                          <Building2 className="h-4 w-4 text-navy-900" />
                          <span>Company Name</span>
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your Company"
                          className="border-gray-300 focus:border-navy-900 focus:ring-navy-900"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="productType" className="flex items-center space-x-2">
                          <Package className="h-4 w-4 text-navy-900" />
                          <span>Product Type</span>
                        </Label>
                        <Input
                          id="productType"
                          name="productType"
                          value={formData.productType}
                          onChange={handleChange}
                          placeholder="e.g., Custom boxes"
                          className="border-gray-300 focus:border-navy-900 focus:ring-navy-900"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="quantity">Estimated Quantity</Label>
                        <Input
                          id="quantity"
                          name="quantity"
                          value={formData.quantity}
                          onChange={handleChange}
                          placeholder="e.g., 1000 units"
                          className="border-gray-300 focus:border-navy-900 focus:ring-navy-900"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your packaging requirements..."
                        rows={5}
                        required
                        className="border-gray-300 focus:border-navy-900 focus:ring-navy-900 resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-navy-900 hover:bg-navy-800 text-white text-lg py-6"
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Side Info - Takes 2 columns */}
            <div className="lg:col-span-2 space-y-6">
              {/* Why Contact Us */}
              <Card className="border-2">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-6 w-6 text-navy-900" />
                    <h3 className="text-xl font-bold text-navy-900">What You Get</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-navy-900 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Response Time */}
              <Card className="border-2 bg-navy-900 text-white">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center space-x-3">
                    <MessageSquare className="h-6 w-6" />
                    <h3 className="text-xl font-bold">Quick Response</h3>
                  </div>
                  <p className="text-gray-200">
                    Our team typically responds to inquiries within 24 hours during business days.
                  </p>
                  <p className="text-sm text-gray-300">
                    For urgent requirements, call us directly at <strong>+91 88790 49450</strong>
                  </p>
                </CardContent>
              </Card>

              {/* Office Hours */}
              <Card className="border-2">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center space-x-3">
                    <Clock className="h-6 w-6 text-navy-900" />
                    <h3 className="text-xl font-bold text-navy-900">Office Hours</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monday - Friday:</span>
                      <span className="text-navy-900 font-medium">9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Saturday:</span>
                      <span className="text-navy-900 font-medium">9:00 AM - 2:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sunday:</span>
                      <span className="text-navy-900 font-medium">Closed</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
